import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  Divider,
  Button,
  Snackbar,
  Alert,
  useTheme as useMuiTheme,
  createTheme,
  Breadcrumbs,
  Link
} from '@mui/material';
import { 
  Home as HomeIcon, 
  Settings as SettingsIcon, 
  NavigateNext as NavigateNextIcon,
  DisplaySettings as DisplaySettingsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { YouTubePlaylistConfig, HorarioConfig, ClimaConfig, DataConfig } from '../components/painel-config';
import { useTheme } from '../context/ThemeContext';
import useSidebar from '../hooks/useSidebar';

const PainelConfiguracao = () => {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { isDarkMode } = useTheme();
  const baseTheme = useMuiTheme();
  const navigate = useNavigate();
  
  // Criando um tema personalizado
  const customTheme = createTheme({
    ...baseTheme,
    typography: {
      fontFamily: [
        'Montserrat',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
    },
  });
  
  const [config, setConfig] = useState({
    youtube: {
      playlistId: 'PLRDNddkJ9BTDGn6IpkvFewfv3cnWlOJlP'
    },
    horario: {
      formato24h: true,
      exibirSegundos: false
    },
    clima: {
      unidade: 'C' // 'C' para Celsius, 'F' para Fahrenheit
    },
    data: {
      formato: 'DD/MM/AAAA' // 'DD/MM/AA', 'DD/MM/AAAA', 'MM/DD/AA', 'MM/DD/AAAA', 'DD de MMMM de AAAA'
    }
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Carregar configurações salvas ao iniciar
  useEffect(() => {
    const savedConfig = localStorage.getItem('painelChamada_configCompleta');
    if (savedConfig) {
      try {
        const parsedConfig = JSON.parse(savedConfig);
        setConfig(prevConfig => ({
          ...prevConfig,
          ...parsedConfig
        }));
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
        setSnackbar({
          open: true,
          message: 'Erro ao carregar configurações salvas',
          severity: 'error'
        });
      }
    }
  }, []);

  // Atualizar a configuração do YouTube
  const handleYouTubeConfigChange = (youtubeConfig) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      youtube: youtubeConfig
    }));
  };

  // Atualizar a configuração do horário
  const handleHorarioConfigChange = (horarioConfig) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      horario: horarioConfig
    }));
  };

  // Atualizar a configuração do clima
  const handleClimaConfigChange = (climaConfig) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      clima: climaConfig
    }));
  };

  // Atualizar a configuração da data
  const handleDataConfigChange = (dataConfig) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      data: dataConfig
    }));
  };

  // Salvar todas as configurações
  const handleSaveConfig = () => {
    try {
      localStorage.setItem('painelChamada_configCompleta', JSON.stringify(config));
      setSnackbar({
        open: true,
        message: 'Configurações salvas com sucesso',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar configurações',
        severity: 'error'
      });
    }
  };

  // Fechar o snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  // Função para alternar a sidebar
  const handleSidebarToggle = () => {
    toggleSidebar();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar
        sidebarOpen={sidebarOpen}
        customTheme={customTheme}
      />

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Header
          sidebarOpen={sidebarOpen}
          handleSidebarToggle={handleSidebarToggle}
          customTheme={customTheme}
        />

        <Container maxWidth="xxl" sx={{ mt: 10, mb: 4 }}>
          {/* Breadcrumbs para navegação */}
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            aria-label="breadcrumb"
            sx={{ mb: 3 }}
          >
            <Link 
              color="inherit" 
              onClick={(e) => {
                e.preventDefault();
                navigate('/dashboard');
              }}
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                textDecoration: 'none',
                color: 'text.primary',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Dashboard
            </Link>
            <Link 
              color="inherit" 
              onClick={(e) => {
                e.preventDefault();
                navigate('/configuracoes');
              }}
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                textDecoration: 'none',
                color: 'text.primary',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              <SettingsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Configurações
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
              <DisplaySettingsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Painel de Chamada
            </Typography>
          </Breadcrumbs>

          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Configuração do Painel de Chamada
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Personalize como o painel de chamada de senhas será exibido para os usuários.
            </Typography>

            <Divider sx={{ my: 3 }} />

            <YouTubePlaylistConfig 
              config={config.youtube} 
              onChange={handleYouTubeConfigChange} 
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" gutterBottom>
              Configuração dos Widgets
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mt: 3 }}>
              <HorarioConfig 
                config={config.horario} 
                onChange={handleHorarioConfigChange} 
              />
              
              <ClimaConfig 
                config={config.clima} 
                onChange={handleClimaConfigChange} 
              />
              
              <DataConfig 
                config={config.data} 
                onChange={handleDataConfigChange} 
              />
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                size="large" 
                onClick={handleSaveConfig}
                sx={{ px: 4 }}
              >
                Salvar Configurações
              </Button>
            </Box>
          </Paper>
        </Container>

        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={6000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default PainelConfiguracao; 